import { config } from "../Environment";
import type { MmObjectType } from "./mentalModels";
import type { NowMode } from "./now";

// Smart capture (spec 2026-11-02-smart-capture-design.md §4). Mirrors
// ClaryNext-BE utils/capture/constants.js.

export type CaptureKind = "action" | "insight" | "mental_model" | "project" | "quality";
export const CAPTURE_KINDS: CaptureKind[] = ["action", "insight", "mental_model", "project", "quality"];

export type CaptureDue = "today" | "tomorrow" | "this_week" | "next_week";
export const CAPTURE_DUES: CaptureDue[] = ["today", "tomorrow", "this_week", "next_week"];

export const MM_TYPES: MmObjectType[] = [
  "belief", "fear", "goal", "value", "attitude", "self_image", "assumption",
  "fact", "conflict", "memory", "proxy", "theory", "decision", "other",
];

export type UnderstandSource = "home" | "capture_page" | "share" | "shortcut";
export type CaptureSource = UnderstandSource | "seeder" | "body";
export type CaptureLocale = "en" | "he" | "de";

export interface CaptureItem {
  text: string;
  kind: CaptureKind;
  mmType?: MmObjectType;
  spaceId?: string;
  projectId?: string;
  /** A part that joins the project this capture creates (whole kind "project"). */
  inNewProject?: boolean;
  due?: CaptureDue;
}

export interface UnderstoodWhole {
  kind: CaptureKind;
  /** For a mental model its type; for other kinds the thought underneath (used by "what's underneath"). */
  mmType?: MmObjectType;
  spaceId?: string;
  projectId?: string;
  /** Kind "project" that is really one of the person's existing projects. */
  sameProjectId?: string;
  due?: CaptureDue;
}

export interface UnderstoodPart {
  text: string;
  kind: CaptureKind;
  mmType?: MmObjectType;
  due?: CaptureDue;
}

export interface BodySuggestion {
  theme: string;
  captureIds: string[];
  includesNew: boolean;
}

export interface FallbackResponse { fallback: true }
export interface DistressResponse { distress: true }
export interface UnderstoodResponse {
  whole: UnderstoodWhole;
  parts: UnderstoodPart[];
  reflective: boolean;
  distress: false;
  body?: BodySuggestion;
  mode: NowMode | null;
  modeWhy?: string;
}
export type UnderstandResponse = FallbackResponse | DistressResponse | UnderstoodResponse;

export const isFallback = (r: UnderstandResponse): r is FallbackResponse =>
  (r as { fallback?: boolean }).fallback === true;
export const isDistress = (r: UnderstandResponse): r is DistressResponse =>
  (r as { distress?: boolean }).distress === true;

export interface CommitRequest {
  text: string;
  source: CaptureSource;
  locale: CaptureLocale;
  whole: CaptureItem;
  parts: CaptureItem[];
  linkCaptureIds?: string[];
  fromDistress?: boolean;
}

export interface CommitResponse {
  captureId: string;
  items: { kind: CaptureKind; id: string; mmType?: MmObjectType }[];
}

async function postJson<T>(retryFetch: Function, path: string, body: unknown): Promise<T> {
  const response = await retryFetch(`${config.API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    let code = response.statusText;
    try {
      const data = await response.json();
      if (data && data.error) code = data.error;
    } catch {
      /* keep statusText */
    }
    throw new Error(`${path} failed: ${code}`);
  }
  return response.json();
}

export const understandCapture = (
  retryFetch: Function,
  body: { text: string; locale: CaptureLocale; source: UnderstandSource }
): Promise<UnderstandResponse> => postJson<UnderstandResponse>(retryFetch, "/api/capture/understand", body);

export const commitCapture = (retryFetch: Function, body: CommitRequest): Promise<CommitResponse> =>
  postJson<CommitResponse>(retryFetch, "/api/capture/commit", body);
